import type { EditorState, Store } from '../state/store';
import { isPresenceViewport } from './presence';
import type { ParticipantTarget, PresenceEvent, PresenceViewport } from './protocol';
import type { PresenceSource } from './presenceSource';
import { interpolateViewport } from './followMotion';
import { t } from '../i18n/I18n';

export type FollowTarget = ParticipantTarget;
export type FollowSnapshot = { target: FollowTarget | null; status: 'idle' | 'transition' | 'following'; reason: string | null };
export type FollowSurface = { apply(event: PresenceEvent): boolean; key(): string; release?(): void };
/** How long the one you followed must stay missing before the notice says they left: the session's presence heartbeat,
 * so an `access_changed` that clears everyone for a reconnect is not taken for a departure. */
export const LEAVE_CONFIRM_MS = 5000;
type FollowClock = { setTimeout(fn: () => void, ms: number): unknown; clearTimeout(handle: unknown): void };
const key = (target: FollowTarget) => JSON.stringify([target.userId, target.clientId]);
const viewKey = (state: EditorState) => JSON.stringify([state.projectId, state.activeLevelIndex, state.mode, state.planSurface,
  state.wallView, state.firstPerson, state.wallMode, state.showCeiling, state.isolatedRoom]);

/** Owns selection and invalidation; renderers only ever receive the latest still-selected packet. */
export class FollowController {
  private state: FollowSnapshot = { target: null, status: 'idle', reason: null };
  private listeners = new Set<() => void>();
  private stops: (() => void)[];
  private context = '';
  private applied = '';
  private applying = false;
  /** Who is followed, by name, to say so when they leave the project. */
  private name = '';
  private leaving: unknown = null;
  private unavailable = '';
  private readonly clock: FollowClock;
  private disposed = false;
  private motion = 0;
  private displayed: PresenceEvent | null = null;

  constructor(private readonly options: { store: Store; session: PresenceSource; surface: FollowSurface; clock?: FollowClock }) {
    this.clock = options.clock ?? { setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: handle => clearTimeout(handle as ReturnType<typeof setTimeout>) };
    let previous = viewKey(options.store.get());
    this.stops = [options.session.subscribe(() => this.refresh()), options.store.subscribe(() => {
      const state = options.store.get(), next = viewKey(state), changed = next !== previous;
      previous = next;
      if (this.applying) return;
      if (this.state.target && (changed || state.showGallery || state.cameraMode || state.showProperties || state.showCatalog
        || state.showLighting || state.materialTarget || state.showVariations || state.tool !== 'navigate')) this.stop();
      else this.refresh();
    })];
  }
  getSnapshot = (): FollowSnapshot => this.state;
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private set(state: FollowSnapshot): void {
    this.state = state;
    for (const listener of this.listeners) listener();
  }
  private sessionContext(): string {
    const snapshot = this.options.session.get().snapshot;
    return JSON.stringify([snapshot?.documentId, snapshot?.generation, snapshot?.layoutVersion, snapshot?.self.userId]);
  }
  private blocked(): string | null {
    const { store, session } = this.options, state = session.get();
    if (state.pendingCommandId) return t('#CollabFinishOrCancelChange');
    if (state.status !== 'ready') return t('#CollabFollowAfterConnecting');
    if (store.documentBusy || store.get().isDragging) return t('#CollabFinishOrCancelChange');
    return null;
  }
  private event(target: FollowTarget): PresenceEvent | undefined {
    const targetKey = key(target);
    return this.options.session.get().participants.find(person => key(person) === targetKey);
  }
  private eligible(target: FollowTarget): string | null {
    const { session } = this.options, snapshot = session.get().snapshot;
    const visited = new Set([key({ userId: snapshot?.self.userId ?? '', clientId: session.clientId })]);
    let cursor: FollowTarget | null = target;
    while (cursor) {
      const id = key(cursor);
      if (visited.has(id)) return t('#CollabFollowLoop');
      visited.add(id); cursor = this.event(cursor)?.viewport?.following ?? null;
    }
    const event = this.event(target), level = event && snapshot?.scene.levels[event.floor.slot];
    if (!event?.viewport || !level || event.generation !== snapshot?.generation || event.floor.layoutVersion !== snapshot.layoutVersion
      || !isPresenceViewport(event.viewport, event.mode, level)) return t('#CollabMemberViewUnavailable');
    return null;
  }
  start(target: FollowTarget): boolean {
    if (this.disposed) return false;
    const reason = this.blocked() ?? this.eligible(target);
    if (reason) { this.stop(reason); return false; }
    this.context = this.sessionContext(); this.applied = ''; this.name = this.event(target)?.displayName ?? '';
    this.set({ target: { ...target }, status: 'transition', reason: null });
    this.refresh(); return this.state.target !== null;
  }
  stop(reason: string | null = null): void {
    if (this.motion) cancelAnimationFrame(this.motion);
    this.motion = 0; this.displayed = null;
    this.applied = ''; this.context = '';
    if (this.leaving && reason !== this.unavailable) { this.clock.clearTimeout(this.leaving); this.leaving = null; }
    const changed = !!this.state.target || this.state.reason !== reason;
    this.state = { target: null, status: 'idle', reason };
    this.options.surface.release?.();
    if (changed) this.set(this.state);
  }
  refresh(): void {
    if (this.disposed || this.applying || !this.state.target) return;
    const changed = this.sessionContext() !== this.context, blocked = changed ? null : this.blocked();
    const reason = changed ? t('#CollabSessionChanged') : blocked ?? this.eligible(this.state.target);
    if (reason && !changed && !blocked && this.name && !this.event(this.state.target)) { this.gone(this.state.target, this.name); return; }
    if (reason) { this.stop(reason); return; }
    const event = this.event(this.state.target)!;
    // A packet that only moves the pointer leaves the view as it is: the frame is applied again when it changes.
    const token = JSON.stringify([key(event), event.viewport, this.options.surface.key()]);
    if (token === this.applied) return;
    this.applying = true;
    let ready = false;
    try { ready = this.apply(event); } finally { this.applying = false; }
    if (ready) {
      this.applied = JSON.stringify([key(event), event.viewport, this.options.surface.key()]);
      if (this.state.status !== 'following') this.set({ ...this.state, status: 'following' });
    } else if (this.state.status !== 'transition') this.set({ ...this.state, status: 'transition' });
  }
  /** Following stops at once; the notice names the departure only once the person stays missing past the heartbeat. */
  private gone(target: FollowTarget, name: string): void {
    this.unavailable = t('#CollabMemberViewUnavailable');
    this.stop(this.unavailable);
    if (this.leaving) this.clock.clearTimeout(this.leaving);
    this.leaving = this.clock.setTimeout(() => {
      this.leaving = null;
      if (!this.state.target && this.state.reason === this.unavailable && !this.event(target)) this.set({ ...this.state, reason: t('#WebFollowLeftProject', name) });
    }, LEAVE_CONFIRM_MS);
  }
  private apply(event: PresenceEvent): boolean {
    const previous = this.displayed;
    if (this.motion) cancelAnimationFrame(this.motion);
    this.motion = 0;
    const animate = previous?.viewport && event.viewport && previous.userId === event.userId && previous.clientId === event.clientId
      && previous.floor.slot === event.floor.slot && previous.viewport.kind === event.viewport.kind
      && typeof requestAnimationFrame === 'function' && !(typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
    if (!animate) {
      const ready = this.options.surface.apply(event);
      if (ready) this.displayed = event;
      return ready;
    }
    const from = previous!.viewport!, target = event.viewport!, started = performance.now();
    const step = (now: number) => {
      this.motion = 0;
      if (this.disposed || !this.state.target) return;
      const progress = Math.min(1, (now - started) / 100);
      const value = { ...event, viewport: interpolateViewport(from, target, progress) };
      this.applying = true;
      let ready = false;
      try { ready = this.options.surface.apply(value); } finally { this.applying = false; }
      if (!ready) { this.applied = ''; return; }
      this.displayed = value;
      if (progress < 1) this.motion = requestAnimationFrame(step);
    };
    this.motion = requestAnimationFrame(step);
    return true;
  }
  /** Undefined defers an unready renderer; null clears an invalid current view. */
  publication(localViewport: unknown): PresenceViewport | null | undefined {
    this.refresh();
    let viewport = localViewport as PresenceViewport | null | undefined;
    if (this.state.target) {
      const targetViewport = this.event(this.state.target)?.viewport;
      viewport = targetViewport ? { ...targetViewport, following: { ...this.state.target } } : null;
    }
    const { store } = this.options, state = store.get(), level = store.level;
    const kind = state.mode === 'scene' ? state.firstPerson ? 'walk' : 'orbit' : state.planSurface === 'wall' ? 'wall' : state.planSurface === 'ceiling' ? 'ceiling' : 'floor';
    if (!viewport || viewport.kind !== kind || viewport.kind === 'wall'
      && (viewport.wallId !== state.wallView?.wallId || viewport.side !== state.wallView?.side)) return undefined;
    const mode = state.mode === 'scene' ? 'scene' : state.planSurface === 'wall' ? 'wall' : 'plan';
    return level && isPresenceViewport(viewport, mode, level) ? viewport : null;
  }
  dispose(): void {
    this.disposed = true; this.stop();
    for (const stop of this.stops) stop();
    this.listeners.clear();
  }
}
